import React, { useCallback, useEffect, useState } from 'react';
import ticketService from '../../services/ticketService';
import { useTicketsRealtime } from '../../lib/realtime';
import { useOrgContextStore } from '../../stores/orgContextStore';
import { orgBasePath } from '../../lib/orgPath';
import type { TicketCounts } from '../../types/ticket';

const ZERO: TicketCounts = { assigned_to_me: 0, unassigned: 0, open: 0 };

/**
 * Open-ticket counts for the header. Inside an org mount the request runs in that org's scope;
 * under /all it is the merged count over every granted organization (the API answers the merged
 * read for multi-org roles without a header). Refreshed by the tickets realtime room of the
 * active org, and on a 60 s poll as the fallback.
 */
export function useTicketCounts(): TicketCounts {
  const [counts, setCounts] = useState<TicketCounts>(ZERO);
  const { activeOrg, myOrgs } = useOrgContextStore();
  const inAllMode = orgBasePath() === '/all';

  const load = useCallback(async () => {
    try {
      const res = await ticketService.getCounts();
      setCounts(res.data.data ?? ZERO);
    } catch {
      /* badge is decoration; a failed count shows nothing */
    }
  }, []);

  useEffect(() => {
    void load();
    const t = window.setInterval(() => void load(), 60_000);
    return () => window.clearInterval(t);
  }, [load]);

  // One live subscription: the active org, or the first granted org under /all (the others
  // reach the badge through the poll — a badge does not warrant N sockets rooms).
  const liveOrgId = inAllMode ? myOrgs[0]?.id ?? null : activeOrg?.id ?? null;
  useTicketsRealtime(liveOrgId, () => void load());

  return counts;
}

/** The count pill inside a nav item. Hidden at zero, so the header stays quiet. */
const TicketBadge: React.FC = () => {
  const counts = useTicketCounts();
  const n = counts.assigned_to_me;
  const unread = counts.unread ?? 0;
  if (!n && !unread) return null;
  return (
    <span className="ml-1.5 inline-flex items-center gap-1">
      {n > 0 && (
        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-black text-white rounded-full leading-none" title={`${n} open ticket${n === 1 ? '' : 's'} assigned to you`}>
          {n > 99 ? '99+' : n}
        </span>
      )}
      {unread > 0 && <span className="w-2 h-2 rounded-full bg-blue-500" title={`${unread} ticket${unread === 1 ? '' : 's'} with new comments`} />}
    </span>
  );
};

export default TicketBadge;
